import { z } from 'zod';
import { Country } from '../enums.js';
/**
 * Which vendor carries SMS to a country's clients (doc 21 D3).
 *
 * ── Where this setting lives, and why ────────────────────────────────────────
 * In code, in the shared package — NOT in Firestore, and not per business.
 *
 * It is a fact about *our vendor contracts*, not about a tenant: every Polish
 * business is served by whichever aggregator we hold an account with, and a
 * tenant has no more business choosing it than choosing which datacentre their
 * data sits in. Keeping it here buys three things Firestore could not:
 * `tsc` proves every `Country` has an entry, a test asserts the routing, and
 * changing a vendor is a reviewable diff rather than a silent console edit that
 * nobody can date afterwards. Same reasoning, and the same shape, as
 * `COUNTRY_DEFAULTS` (doc 17).
 *
 * Deliberately SEPARATE from `COUNTRY_DEFAULTS.provider`, which says who sells
 * that country's *numbers*: we buy Polish numbers from Telnyx and send Polish
 * SMS through a Polish aggregator, and there is no reason those must ever be
 * the same vendor.
 *
 * The one dynamic hatch is `SMS_PROVIDER_OVERRIDE` (§ below), an env variable
 * read at boot — for staged rollout and instant rollback, where waiting for a
 * code review is the wrong answer.
 */
export const SmsProvider = z.enum(['twilio', 'hostedsms']);
export const SMS_PROVIDER_BY_COUNTRY = {
    // ~0.07 PLN/SMS against Twilio's ~0.17 for the same delivery, and the sender
    // field is registered with the Polish networks rather than routed in from
    // outside. See doc 21 D3 for the rate table this was chosen from.
    PL: 'hostedsms',
    // No local aggregator relationship, and Twilio's credentials, SDK and
    // signature validation are already in the tree — so FR/DE cost nothing to
    // support and would cost a vendor contract each to optimise.
    DE: 'twilio',
    FR: 'twilio',
};
/**
 * Vendor for destinations outside the business's own country — **off by
 * default**, and that default is a fact about the estate, not caution.
 *
 * This used to be a hard-coded `'twilio'`, on the reasoning that Twilio reaches
 * anywhere and costs nothing extra to keep because the account already exists.
 * The second half of that was wrong: the account authenticates (the gateway
 * requires it at boot) but **Twilio is not in use** — no SMS geo-permissions,
 * no messaging spend, and it is the vendor we are moving away from, not
 * towards. Routing a foreign number to it meant routing it to a carrier that
 * would reject the send, which is worse than the cheap-rate problem the
 * fallback existed to avoid: a silent `failed` row instead of a message.
 *
 * So there is no automatic global vendor. With none configured, a foreign
 * destination goes through the BUSINESS's own country vendor and the ledger
 * records whatever the vendor says. That is the honest option: a Polish
 * aggregator may refuse or surcharge an international number, but "we tried and
 * here is their answer" beats both a guess about their policy and a send to a
 * carrier known to be switched off.
 *
 * Set `SMS_GLOBAL_PROVIDER=twilio` to restore the old behaviour the moment
 * Twilio messaging is actually enabled. One env line, no deploy of new code.
 */
export function parseGlobalSmsProvider(raw) {
    const parsed = SmsProvider.safeParse((raw ?? '').trim());
    return parsed.success ? parsed.data : null;
}
/**
 * Per-country override, parsed from `SMS_PROVIDER_OVERRIDE` (e.g.
 * `PL=twilio,DE=twilio`). Exists for exactly one job: flipping a country back
 * to the previous vendor without a deploy when a new aggregator misbehaves in
 * production. Unset in normal operation.
 */
export function parseSmsProviderOverride(raw) {
    const out = {};
    for (const pair of (raw ?? '').split(',')) {
        const [country, provider] = pair.split('=').map((s) => s.trim());
        const c = Country.safeParse(country);
        const p = SmsProvider.safeParse(provider);
        if (c.success && p.success)
            out[c.data] = p.data;
    }
    return out;
}
/**
 * Pick the carrier for one message.
 *
 * Routing is by the BUSINESS's country, because that is what our vendor
 * accounts are scoped to. A destination in another country — or one we cannot
 * attribute to a country at all — uses the configured international vendor if
 * there is one, and otherwise stays on the business's own vendor rather than
 * being routed to a carrier that is switched off (see
 * {@link parseGlobalSmsProvider}).
 */
export function smsProviderFor(businessCountry, destinationIso, opts = {}) {
    const domestic = opts.overrides?.[businessCountry] ?? SMS_PROVIDER_BY_COUNTRY[businessCountry];
    if (destinationIso === businessCountry)
        return domestic;
    return opts.globalProvider ?? domestic;
}
//# sourceMappingURL=sms.js.map